import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";

const navLinkBase =
  "relative text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [topicOpen, setTopicOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState<string | null>(null);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
    setTopicOpen(false);
    setMobileGroup(null);
  }, [location.pathname]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const solidText = transparent ? "text-background-50" : "text-foreground-800";
  const solidTextHover = transparent ? "hover:text-accent-300" : "hover:text-primary-600";

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        transparent
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200/70"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="mx-auto max-w-content h-16 md:h-20 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2 cursor-pointer shrink-0">
            <span
              className={`w-9 h-9 rounded-md flex items-center justify-center font-heading font-bold text-lg ${
                transparent ? "bg-background-50 text-primary-700" : "bg-primary-600 text-background-50"
              }`}
            >
              黑
            </span>
            <span className="leading-tight">
              <span className={`block font-heading font-bold text-lg tracking-wide ${solidText}`}>
                黑人
              </span>
              <span
                className={`block text-[10px] tracking-[0.2em] ${
                  transparent ? "text-background-200/90" : "text-foreground-500"
                }`}
              >
                CULTURE ARCHIVE
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            <NavLink to="/" className={({ isActive }) => `${navLinkBase} ${isActive ? "text-accent-500" : `${solidText} ${solidTextHover}`}`}>
              首页
            </NavLink>

            <div className="relative" onMouseEnter={() => setCatOpen(true)} onMouseLeave={() => setCatOpen(false)}>
              <button
                type="button"
                className={`${navLinkBase} ${solidText} ${solidTextHover} flex items-center gap-1`}
              >
                分类
                <i className="ri-arrow-down-s-line text-base" />
              </button>
              {catOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
                  <div className="w-56 bg-background-50 rounded-lg border border-background-200 shadow-sm overflow-hidden animate-soft-in">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                      >
                        <i className={`${cat.icon} text-base text-accent-500`} />
                        <span className="flex-1">{cat.name}</span>
                        <span className="text-xs text-foreground-400">{cat.en}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" onMouseEnter={() => setTopicOpen(true)} onMouseLeave={() => setTopicOpen(false)}>
              <button
                type="button"
                className={`${navLinkBase} ${solidText} ${solidTextHover} flex items-center gap-1`}
              >
                专题
                <i className="ri-arrow-down-s-line text-base" />
              </button>
              {topicOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
                  <div className="w-64 bg-background-50 rounded-lg border border-background-200 shadow-sm overflow-hidden animate-soft-in">
                    {topics.map((topic) => (
                      <Link
                        key={topic.id}
                        to={`/topic/${topic.id}`}
                        className="block px-4 py-3 border-b border-background-100 last:border-0 hover:bg-primary-50 transition-colors cursor-pointer"
                      >
                        <span className="block text-sm font-medium text-foreground-800">{topic.title}</span>
                        <span className="block text-xs text-foreground-400 mt-0.5">{topic.subtitle}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/search"
              className={({ isActive }) => `${navLinkBase} ${isActive ? "text-accent-500" : `${solidText} ${solidTextHover}`}`}
            >
              搜索
            </NavLink>

            <NavLink
              to="/about"
              className={({ isActive }) => `${navLinkBase} ${isActive ? "text-accent-500" : `${solidText} ${solidTextHover}`}`}
            >
              关于我们
            </NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/search"
              aria-label="搜索"
              className={`hidden sm:flex w-10 h-10 items-center justify-center rounded-full transition-colors cursor-pointer ${
                transparent
                  ? "text-background-50 hover:bg-background-50/15"
                  : "text-foreground-600 hover:bg-primary-50 hover:text-primary-700"
              }`}
            >
              <i className="ri-search-line text-lg" />
            </Link>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMobileOpen((v) => !v)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                transparent ? "text-background-50 hover:bg-background-50/15" : "text-foreground-700 hover:bg-primary-50"
              }`}
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 animate-soft-in max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link to="/" className="block px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-primary-50 cursor-pointer">
              首页
            </Link>

            <div>
              <button
                type="button"
                onClick={() => setMobileGroup((g) => (g === "cat" ? null : "cat"))}
                className="w-full flex items-center justify-between px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-primary-50 cursor-pointer"
              >
                <span>分类</span>
                <i className={`ri-arrow-down-s-line transition-transform ${mobileGroup === "cat" ? "rotate-180" : ""}`} />
              </button>
              {mobileGroup === "cat" && (
                <div className="pl-3 py-1 space-y-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-foreground-600 hover:bg-primary-50 cursor-pointer"
                    >
                      <i className={`${cat.icon} text-accent-500`} />
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div>
              <button
                type="button"
                onClick={() => setMobileGroup((g) => (g === "topic" ? null : "topic"))}
                className="w-full flex items-center justify-between px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-primary-50 cursor-pointer"
              >
                <span>专题</span>
                <i className={`ri-arrow-down-s-line transition-transform ${mobileGroup === "topic" ? "rotate-180" : ""}`} />
              </button>
              {mobileGroup === "topic" && (
                <div className="pl-3 py-1 space-y-1">
                  {topics.map((topic) => (
                    <Link
                      key={topic.id}
                      to={`/topic/${topic.id}`}
                      className="block px-3 py-2.5 rounded-md text-sm text-foreground-600 hover:bg-primary-50 cursor-pointer"
                    >
                      {topic.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/search" className="block px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-primary-50 cursor-pointer">
              搜索
            </Link>
            <Link to="/about" className="block px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-primary-50 cursor-pointer">
              关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}